// src/pages/ServiceManagement/tabs/VersionsTab.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { servicesApi } from '../../../services/apiClient';

const VersionsTab = ({ serviceId, onUpdate }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  const [formData, setFormData] = useState({ path: '', version: '' });
  const [saving, setSaving] = useState(false);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const response = await servicesApi.getVersions(serviceId);
      if (response.data.success) {
        setVersions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading versions:', error);
      enqueueSnackbar('Failed to load versions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVersions();
  }, [serviceId]);

  const handleOpenDialog = (version = null) => {
    if (version) {
      setEditingVersion(version);
      setFormData({ path: version.path, version: version.version });
    } else {
      setEditingVersion(null);
      setFormData({ path: '', version: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVersion(null);
    setFormData({ path: '', version: '' });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editingVersion) {
        await servicesApi.updateVersion(editingVersion.id, formData);
        enqueueSnackbar('Version updated successfully', { variant: 'success' });
      } else {
        await servicesApi.createVersion(serviceId, formData);
        enqueueSnackbar('Version created successfully', { variant: 'success' });
      }
      loadVersions();
      handleCloseDialog();
      onUpdate();
    } catch (error) {
      console.error('Error saving version:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to save version', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (versionId) => {
    if (!window.confirm('Are you sure you want to delete this version?')) return;

    try {
      await servicesApi.deleteVersion(versionId);
      enqueueSnackbar('Version deleted successfully', { variant: 'success' });
      loadVersions();
      onUpdate();
    } catch (error) {
      console.error('Error deleting version:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to delete version', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Service Versions</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Version
        </Button>
      </Box>

      {versions.length === 0 ? (
        <Alert severity="info">
          No versions defined yet. Add a version to specify available service paths.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Path</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((version) => (
                <TableRow key={version.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="500">
                      {version.version}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                      {version.path}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {version.createdAt ? new Date(version.createdAt).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(version)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(version.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVersion ? 'Edit Version' : 'Add Version'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Version"
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              helperText="Version number (e.g., 1.0.0, 2.1.3)"
              required
            />
            <TextField
              fullWidth
              label="Path"
              value={formData.path}
              onChange={(e) => setFormData({ ...formData, path: e.target.value })}
              helperText="File path or URL to the service version"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.path || !formData.version || saving}
            startIcon={saving && <CircularProgress size={20} />}
          >
            {editingVersion ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VersionsTab;
